import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels";
import { api, API } from "../lib/api";

const DISCIPLINES = [
  { key: "lean",              name: "Lean",              roman: "I",   tag: "Minimum viable. Zero bloat." },
  { key: "six_sigma",         name: "Six Sigma",         roman: "II",  tag: "Defect detection & mitigation." },
  { key: "design_thinking",   name: "Design Thinking",   roman: "III", tag: "Empathy & persona mapping." },
  { key: "systems_thinking",  name: "Systems Thinking",  roman: "IV",  tag: "Data flow & feedback loops." },
  { key: "chaos_engineering", name: "Chaos Engineering", roman: "V",   tag: "Failure mode injection." },
  { key: "agile_scrum",       name: "Agile / Scrum",     roman: "VI",  tag: "Sprint cadence & deliverables." },
];

const PHASES = ["Discovery", "HEXIS Audit", "Generation", "Chaos Test", "Deploy"];

export default function Sprint() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const refresh = async () => {
    const { data } = await api.get(`/projects/${projectId}`);
    setProject(data);
    return data;
  };

  useEffect(() => {
    refresh().catch(() => nav("/laboratory")).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2600); };

  const setPhase = async (n) => {
    if (!project) return;
    // guard: phase 5 requires chaos.passed
    if (n === 5 && !project.chaos?.passed) { showToast("Chaos test must pass first"); return; }
    await api.patch(`/projects/${projectId}`, { phase: n });
    await refresh();
  };

  if (loading || !project) {
    return <div style={{ padding: 80 }} className="font-mono">Loading sprint...</div>;
  }

  const phase = project.phase || 1;

  return (
    <div data-testid="sprint-page" style={{ padding: "32px 4vw 60px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ maxWidth: 720 }}>
          <div className="overline">Sprint · {project.project_id}</div>
          <h1 className="font-serif" style={{ fontSize: "clamp(32px, 4vw, 48px)", lineHeight: 1.05, margin: "8px 0 4px" }}>
            {project.name}
          </h1>
          <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", fontSize: 16 }}>
            {project.description || "— no description —"}
          </p>
        </div>
        <button className="btn-ghost" onClick={() => nav("/laboratory")} data-testid="back-to-lab">← Laboratory</button>
      </div>

      {/* Stepper */}
      <div className="stepper" data-testid="phase-stepper" style={{ marginBottom: 28 }}>
        {PHASES.map((p, i) => {
          const idx = i + 1;
          const active = idx === phase;
          const done = idx < phase || (idx === 4 && project.chaos?.passed) || (idx === 5 && project.deployed);
          return (
            <div
              key={p}
              data-testid={`phase-step-${idx}`}
              className={`stepper-item ${active ? "active" : ""} ${done ? "done" : ""}`}
              onClick={() => setPhase(idx)}
            >
              <span className="font-display" style={{ fontSize: 15, fontStyle: "italic", marginRight: 8 }}>{idx}.</span>
              {p}
            </div>
          );
        })}
      </div>

      {phase === 1 && <PhaseDiscovery project={project} refresh={refresh} />}
      {phase === 2 && <PhaseAudit project={project} refresh={refresh} />}
      {phase === 3 && <PhaseGeneration project={project} refresh={refresh} setPhase={setPhase} />}
      {phase === 4 && <PhaseChaos project={project} refresh={refresh} showToast={showToast} />}
      {phase === 5 && <PhaseDeploy project={project} refresh={refresh} />}

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <button
          data-testid="phase-prev"
          className="btn-ghost"
          disabled={phase === 1}
          onClick={() => setPhase(Math.max(1, phase - 1))}
        >← Previous</button>
        <button
          data-testid="phase-next"
          className="btn-brass"
          disabled={phase === 5}
          onClick={() => setPhase(Math.min(5, phase + 1))}
        >Next Phase →</button>
      </div>

      {toast && <div className="hexis-toast" data-testid="sprint-toast">{toast}</div>}
    </div>
  );
}

/* ---------------- PHASE 1 — Discovery ---------------- */
function PhaseDiscovery({ project, refresh }) {
  const [msg, setMsg] = useState("");
  const [model, setModel] = useState("claude");
  const [busy, setBusy] = useState(false);
  const [streamBuf, setStreamBuf] = useState(""); // live-streaming mentor reply
  const [pendingUser, setPendingUser] = useState(""); // echo user msg instantly
  const scrollRef = useRef(null);
  const messages = project.discovery?.messages || [];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, streamBuf]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!msg.trim() || busy) return;
    const userMsg = msg;
    setMsg("");
    setBusy(true);
    setPendingUser(userMsg);
    setStreamBuf("");

    try {
      const token = localStorage.getItem("hexis_token");
      const res = await fetch(`${API}/llm/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ project_id: project.project_id, model, message: userMsg }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let leftover = "";
      let accumulated = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = leftover + decoder.decode(value, { stream: true });
        const lines = text.split("\n");
        leftover = lines.pop() || "";

        let currentEvent = "message";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            const payload = line.slice(6);
            try {
              const obj = JSON.parse(payload);
              if (currentEvent === "chunk" && obj.text) {
                accumulated += obj.text;
                setStreamBuf(accumulated);
              } else if (currentEvent === "error") {
                throw new Error(obj.detail || "Stream error");
              }
            } catch (parseErr) { /* ignore malformed */ }
          }
        }
      }
      // finalize — persistence already done server-side
      await refresh();
      setStreamBuf("");
      setPendingUser("");
    } catch (ex) {
      const detail = ex?.message || "Oracle silent.";
      alert(model === "deepseek" && /Insufficient Balance|402/i.test(detail)
        ? "DeepSeek API has no balance. Top up at https://platform.deepseek.com/top_up — or toggle to Claude."
        : `Mentor unreachable: ${detail}`);
      setStreamBuf("");
      setPendingUser("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="phase-discovery" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}>
      <div className="glass" style={{ padding: 24, display: "flex", flexDirection: "column", minHeight: 520 }}>
        <div className="overline" style={{ marginBottom: 14 }}>Phase I · Discovery — converse with the Mentor</div>
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, paddingRight: 8 }}>
          {messages.length === 0 && !pendingUser && (
            <div className="bubble-mentor">
              <p>Salutations, Student of the Lyceum. Tell me — what application shall we architect today?
              Describe its purpose, its users, and the shape of its virtue.</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "bubble-user" : "bubble-mentor"}>
              {m.role !== "user" && <div className="overline" style={{ color: "var(--brass)", fontSize: 9, marginBottom: 8 }}>
                The Mentor · {m.model || "claude"}
              </div>}
              <div>{m.content}</div>
            </div>
          ))}
          {pendingUser && (
            <div className="bubble-user" data-testid="pending-user-bubble">{pendingUser}</div>
          )}
          {busy && (
            <div className="bubble-mentor" data-testid="streaming-bubble">
              <div className="overline" style={{ color: "var(--brass)", fontSize: 9, marginBottom: 8 }}>
                The Mentor · {model} {streamBuf ? "" : "deliberates..."}
              </div>
              {streamBuf && <div>{streamBuf}<span style={{ opacity: 0.5 }}>▋</span></div>}
            </div>
          )}
        </div>
        <form onSubmit={send} style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <input
            data-testid="discovery-input"
            className="input-field"
            placeholder="What shall we build?"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            disabled={busy}
          />
          <button data-testid="discovery-send-btn" className="btn-brass" disabled={busy}>
            {busy ? "..." : "Send"}
          </button>
        </form>
      </div>

      <div className="glass" style={{ padding: 22, alignSelf: "flex-start" }}>
        <div className="overline" style={{ marginBottom: 14 }}>Oracle</div>
        <ModelToggle model={model} setModel={setModel} />
        <div className="font-serif" style={{ marginTop: 20, fontSize: 14, color: "var(--parchment-dim)", fontStyle: "italic", lineHeight: 1.6 }}>
          <strong style={{ color: "var(--parchment)" }}>Claude</strong> — swift prose, UI-fluent.<br />
          <strong style={{ color: "var(--parchment)" }}>DeepSeek</strong> — slow reasoning, deep logic.
        </div>
        <div className="rule-parchment" style={{ margin: "20px 0" }} />
        <div className="font-mono" style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.7 }}>
          Fig. III — When the conversation has clarified the app's
          intent and first-user-persona, advance to the HEXIS Audit.
        </div>
      </div>
    </div>
  );
}

/* ---------------- PHASE 2 — HEXIS Audit ---------------- */
function PhaseAudit({ project, refresh }) {
  const [model, setModel] = useState("deepseek");
  const [busy, setBusy] = useState(null); // key being run | 'all' | null
  const [liveStatuses, setLiveStatuses] = useState({}); // key -> 'pending'|'running'|'done'|'error'
  const audit = project.audit || {};

  const runAll = async () => {
    setBusy("all");
    const init = {};
    DISCIPLINES.forEach((d) => { init[d.key] = "pending"; });
    setLiveStatuses(init);

    try {
      const token = localStorage.getItem("hexis_token");
      const res = await fetch(`${API}/llm/audit/stream-all`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ project_id: project.project_id, model }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let leftover = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = leftover + decoder.decode(value, { stream: true });
        const lines = text.split("\n");
        leftover = lines.pop() || "";

        let currentEvent = "message";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            try {
              const obj = JSON.parse(line.slice(6));
              if (currentEvent === "discipline_start") {
                setLiveStatuses((s) => ({ ...s, [obj.discipline]: "running" }));
              } else if (currentEvent === "discipline_done") {
                setLiveStatuses((s) => ({ ...s, [obj.discipline]: obj.ok ? "done" : "error" }));
                // refresh once per completion so card fills in
                await refresh();
              }
            } catch (_) { /* ignore */ }
          }
        }
      }
      setLiveStatuses({});
    } catch (ex) {
      alert("Audit stream failed: " + (ex?.message || "unknown"));
      setLiveStatuses({});
    } finally {
      setBusy(null);
    }
  };

  const runOne = async (key) => {
    setBusy(key);
    try {
      await api.post("/llm/audit", { project_id: project.project_id, model, discipline: key, context: "" });
      await refresh();
    } catch (ex) {
      const detail = ex?.response?.data?.detail || "Audit failed.";
      alert(model === "deepseek" && /Insufficient Balance|402/i.test(detail)
        ? "DeepSeek API has no balance. Top up or switch to Claude."
        : `Audit failed: ${detail}`);
    } finally { setBusy(null); }
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem("hexis_token");
    const res = await fetch(`${API}/projects/${project.project_id}/audit.pdf`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { alert("PDF generation failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${project.name.replace(/[^a-z0-9-_]/gi, "_")}_hexis_audit.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const completed = DISCIPLINES.filter((d) => audit[d.key]?.content).length;

  return (
    <div data-testid="phase-audit">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22, flexWrap: "wrap", gap: 14 }}>
        <div>
          <div className="overline">Phase II · The Six-Discipline Scrutiny</div>
          <h3 className="font-serif" style={{ fontSize: 32, margin: "4px 0 0" }}>
            {completed}/6 pillars examined.
          </h3>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <ModelToggle model={model} setModel={setModel} inline />
          <button
            data-testid="audit-download-pdf-btn"
            className="btn-ghost"
            onClick={downloadPdf}
            disabled={completed === 0}
            style={{ padding: "10px 18px" }}
          >
            ⬇ PDF
          </button>
          <button data-testid="audit-run-all" className="btn-brass" onClick={runAll} disabled={busy !== null}>
            {busy === "all" ? "Examining in parallel..." : "Examine All in Parallel"}
          </button>
        </div>
      </div>

      <div className="rule-parchment" style={{ marginBottom: 28 }} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 20 }}>
        {DISCIPLINES.map((d, i) => {
          const r = audit[d.key];
          const running = busy === d.key || liveStatuses[d.key] === "running";
          const liveStatus = liveStatuses[d.key];
          return (
            <div
              key={d.key}
              className="scroll-card reveal"
              data-testid={`audit-${d.key}`}
              style={{ animationDelay: `${0.05 * i}s`, minHeight: 260 }}
            >
              <span className="marginalia">Ref. 0{i + 1}</span>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div>
                  <div className="overline" style={{ color: "var(--brass)" }}>Pillar {d.roman}</div>
                  <h4 className="font-serif" style={{ fontSize: 28, margin: "8px 0 4px" }}>{d.name}</h4>
                </div>
                {liveStatus && (
                  <span
                    data-testid={`audit-live-status-${d.key}`}
                    className="font-mono"
                    style={{
                      fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase",
                      padding: "3px 7px", border: "1px solid",
                      color: liveStatus === "done" ? "var(--success)" : liveStatus === "error" ? "var(--chaos)" : "var(--brass)",
                      borderColor: liveStatus === "done" ? "var(--success)" : liveStatus === "error" ? "var(--chaos)" : "var(--brass)",
                    }}
                  >
                    {liveStatus === "pending" ? "◎ queued" : liveStatus === "running" ? "● running" : liveStatus === "done" ? "✓ done" : "✗ err"}
                  </span>
                )}
              </div>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--parchment-dim)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 14 }}>
                {d.tag}
              </div>
              {r?.content ? (
                <div
                  className="font-serif"
                  style={{ fontSize: 15, color: "var(--parchment-accent)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}
                >
                  {r.content}
                </div>
              ) : (
                <div className="font-mono" style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>
                  {running ? "↳ The oracle deliberates..." : "Awaiting examination."}
                </div>
              )}
              <div style={{ marginTop: 18 }}>
                <button
                  data-testid={`audit-run-${d.key}`}
                  className="btn-ghost"
                  onClick={() => runOne(d.key)}
                  disabled={busy !== null}
                >
                  {r?.content ? "Re-examine" : "Examine"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- PHASE 3 — Generation ---------------- */
function PhaseGeneration({ project, refresh, setPhase }) {
  const [model, setModel] = useState(project.generation?.model || "claude");
  const [spec, setSpec] = useState(project.description || "");
  const [busy, setBusy] = useState(false);
  const [streamRaw, setStreamRaw] = useState(""); // full raw stream buffer
  const [justGenerated, setJustGenerated] = useState(false); // auto-chain CTA
  const files = project.generation?.files || [];
  const [activeFile, setActiveFile] = useState(files[0]?.path || null);

  useEffect(() => {
    if (files.length && !activeFile) setActiveFile(files[0].path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  const generate = async () => {
    if (!spec.trim() || busy) return;
    setBusy(true);
    setStreamRaw("");
    try {
      const token = localStorage.getItem("hexis_token");
      const res = await fetch(`${API}/llm/generate-code/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ project_id: project.project_id, model, spec }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let leftover = "";
      let accumulated = "";
      let parsedFiles = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = leftover + decoder.decode(value, { stream: true });
        const lines = text.split("\n");
        leftover = lines.pop() || "";

        let currentEvent = "message";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            const payload = line.slice(6);
            try {
              const obj = JSON.parse(payload);
              if (currentEvent === "chunk" && obj.text) {
                accumulated += obj.text;
                setStreamRaw(accumulated);
              } else if (currentEvent === "files" && obj.files) {
                parsedFiles = obj.files;
              } else if (currentEvent === "error") {
                throw new Error(obj.detail || "Generation error");
              }
            } catch (_) { /* ignore */ }
          }
        }
      }

      await refresh();
      if (parsedFiles?.[0]) setActiveFile(parsedFiles[0].path);
      setStreamRaw("");
      setJustGenerated(true);
    } catch (ex) {
      const detail = ex?.message || "Generation failed.";
      alert(model === "deepseek" && /Insufficient Balance|402/i.test(detail)
        ? "DeepSeek API has no balance. Top up or switch to Claude."
        : `Generation failed: ${detail}`);
      setStreamRaw("");
    } finally {
      setBusy(false);
    }
  };

  const current = files.find((f) => f.path === activeFile);

  return (
    <div data-testid="phase-generation">
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 18, alignItems: "flex-end" }}>
        <div>
          <div className="overline">Phase III · Generation — the CERN Control Room</div>
          <h3 className="font-serif" style={{ fontSize: 30, margin: "4px 0 0" }}>
            Inscribe the code.
          </h3>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <ModelToggle model={model} setModel={setModel} inline />
          <button data-testid="generation-run-btn" className="btn-brass" onClick={generate} disabled={busy}>
            {busy ? "Generating..." : files.length ? "Re-generate" : "Generate Code"}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label className="label">Generation Spec</label>
        <textarea
          data-testid="generation-spec-input"
          className="input-field" rows={2}
          value={spec} onChange={(e) => setSpec(e.target.value)}
          placeholder="Full-stack MVP: React frontend + Node/Express API for the app described..."
        />
      </div>

      {justGenerated && files.length > 0 && !busy && (
        <div
          data-testid="autochain-cta"
          className="reveal"
          style={{
            marginBottom: 20,
            padding: "18px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
            background: "linear-gradient(100deg, rgba(197,160,89,0.14), rgba(197,160,89,0.04))",
            border: "1px solid var(--brass)",
            borderLeft: "4px solid var(--brass)",
          }}
        >
          <div>
            <div className="overline" style={{ color: "var(--brass)", marginBottom: 6 }}>⚡ Artifact inscribed · {files.length} files</div>
            <div className="font-serif" style={{ fontSize: 20, color: "var(--parchment)", fontStyle: "italic" }}>
              Run the Chaos Test to unlock Deploy — <span style={{ color: "var(--brass)" }}>the crucible awaits.</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              data-testid="autochain-dismiss"
              className="btn-ghost"
              onClick={() => setJustGenerated(false)}
            >
              Dismiss
            </button>
            <button
              data-testid="autochain-chaos-btn"
              className="btn-brass"
              onClick={() => setPhase(4)}
            >
              ↳ Run Chaos Test
            </button>
          </div>
        </div>
      )}

      {busy && streamRaw ? (
        <div style={{ border: "1px solid rgba(197,160,89,0.25)" }}>
          <div className="overline" style={{ padding: "10px 18px", borderBottom: "1px solid rgba(197,160,89,0.2)", color: "var(--brass)" }}>
            <span data-testid="generation-streaming-label">Generating · {streamRaw.length} chars received</span>
          </div>
          <div className="code-view" data-testid="generation-stream-view" style={{ height: 520, border: "none" }}>
            {streamRaw}
            <span style={{ opacity: 0.5 }}>▋</span>
          </div>
        </div>
      ) : files.length === 0 ? (
        <div style={{ border: "1px dashed rgba(197,160,89,0.35)", padding: 60, textAlign: "center" }}>
          <div className="font-display" style={{ fontSize: 42, color: "var(--brass)" }}>Ø</div>
          <div className="font-serif" style={{ fontSize: 22, marginTop: 6 }}>No files yet generated.</div>
          <div className="font-mono" style={{ color: "var(--parchment-dim)", fontSize: 12, marginTop: 10 }}>
            Choose a model above and invoke Generation.
          </div>
        </div>
      ) : (
        <div style={{ height: 560, border: "1px solid rgba(197,160,89,0.25)" }}>
          <PanelGroup direction="horizontal">
            <Panel defaultSize={22} minSize={16}>
              <div style={{ padding: 18, height: "100%", background: "rgba(9,10,15,0.7)", overflowY: "auto" }}>
                <div className="overline" style={{ marginBottom: 12, color: "var(--brass)" }}>Files</div>
                {files.map((f) => (
                  <div
                    key={f.path}
                    data-testid={`file-${f.path}`}
                    onClick={() => setActiveFile(f.path)}
                    className="font-mono"
                    style={{
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontSize: 12,
                      color: f.path === activeFile ? "var(--parchment)" : "var(--parchment-dim)",
                      background: f.path === activeFile ? "rgba(197,160,89,0.08)" : "transparent",
                      borderLeft: f.path === activeFile ? "2px solid var(--brass)" : "2px solid transparent",
                      marginBottom: 2,
                      wordBreak: "break-all",
                    }}
                  >
                    {f.path}
                  </div>
                ))}
              </div>
            </Panel>
            <PanelResizeHandle style={{ width: 1, background: "rgba(197,160,89,0.25)" }} />
            <Panel defaultSize={48} minSize={28}>
              <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <div className="overline" style={{ padding: "10px 18px", borderBottom: "1px solid rgba(197,160,89,0.2)", color: "var(--brass)" }}>
                  {current?.path || "—"}
                </div>
                <div className="code-view" data-testid="code-view" style={{ flex: 1, border: "none" }}>
                  {current?.content || ""}
                </div>
              </div>
            </Panel>
            <PanelResizeHandle style={{ width: 1, background: "rgba(197,160,89,0.25)" }} />
            <Panel defaultSize={30} minSize={20}>
              <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <div className="overline" style={{ padding: "10px 18px", borderBottom: "1px solid rgba(197,160,89,0.2)", color: "var(--brass)" }}>
                  Preview
                </div>
                <iframe
                  data-testid="code-preview-iframe"
                  title="preview"
                  srcDoc={previewHtml(files)}
                  sandbox="allow-scripts"
                  style={{ flex: 1, width: "100%", border: "none", background: "#fff" }}
                />
              </div>
            </Panel>
          </PanelGroup>
        </div>
      )}
    </div>
  );
}

function previewHtml(files) {
  const htmlFile = files.find((f) => /\.html$/i.test(f.path));
  if (htmlFile) return htmlFile.content;
  // Build a minimal preview showing structure
  return `<!doctype html><html><head><meta charset='utf-8'><title>Preview</title>
    <style>
      body{font-family:Georgia,serif;background:#fafaf5;color:#222;padding:32px;line-height:1.6}
      h1{font-family:'Cormorant Garamond',serif;color:#8b6f2b}
      .file{border:1px solid #ccc;padding:14px;margin:10px 0;background:#fff}
      .path{font-family:monospace;color:#8b6f2b;font-size:12px;letter-spacing:.1em;text-transform:uppercase;margin-bottom:8px}
      pre{font-family:monospace;font-size:11px;white-space:pre-wrap;max-height:200px;overflow:auto;margin:0;color:#333}
    </style></head><body>
    <h1>HEXIS Generated Artifact</h1>
    <p>${files.length} file(s) in this sprint:</p>
    ${files.map((f) => `<div class='file'><div class='path'>${esc(f.path)}</div><pre>${esc(String(f.content).slice(0, 1200))}</pre></div>`).join("")}
    </body></html>`;
}
function esc(s) {
  return String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
}

/* ---------------- PHASE 4 — Chaos Engineering ---------------- */
function PhaseChaos({ project, refresh, showToast }) {
  const [running, setRunning] = useState(false);
  const [lines, setLines] = useState([]);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("hexis_chaos_sound") !== "off"; } catch { return true; }
  });
  const [stdoutOpen, setStdoutOpen] = useState(false);
  const chaos = project.chaos || {};

  // Extract RUNTIME probe's captured stdout lines (from detail string "stdout: a | b | c")
  const runtimeProbe = (chaos.results || []).find((r) => r.layer === "RUNTIME");
  const runtimeStdout = (() => {
    if (!runtimeProbe?.detail) return [];
    const m = runtimeProbe.detail.match(/stdout:\s*(.+)$/);
    return m ? m[1].split(" | ").map((s) => s.trim()).filter(Boolean) : [];
  })();

  // Minimal retro-synth chaos SFX using the WebAudio API — no assets.
  const chime = (outcome) => {
    if (!soundOn || typeof window === "undefined") return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = chime._ctx || (chime._ctx = new AC());
      const now = ctx.currentTime;
      const { freq, dur, shape } = outcome === "PASS"
        ? { freq: 880, dur: 0.11, shape: "sine" }
        : outcome === "WARN"
        ? { freq: 523.25, dur: 0.14, shape: "triangle" }
        : outcome === "FAIL"
        ? { freq: 196, dur: 0.22, shape: "sawtooth" }
        : { freq: 330, dur: 0.08, shape: "square" };
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = shape;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.14, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + dur + 0.02);
    } catch (_) { /* swallow — audio is a bonus, never a blocker */ }
  };

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      try { localStorage.setItem("hexis_chaos_sound", next ? "on" : "off"); } catch {}
      return next;
    });
  };

  const run = async () => {
    setRunning(true);
    setLines([]);
    setStdoutOpen(false);
    const push = (text, cls = "") => setLines((l) => [...l, { text, cls }]);

    push("$ hexis chaos inject --project " + project.project_id, "brass");
    push("↳ running real probes: syntax · json · structure · utf · network · vm-sandbox...", "muted");
    chime("boot");
    await sleep(320);

    try {
      const { data } = await api.post("/llm/chaos-test", { project_id: project.project_id });
      for (const r of data.results) {
        const color = r.outcome === "PASS" ? "" : r.outcome === "WARN" ? "warn" : "err";
        push(`  [${r.layer.padEnd(10)}] ${r.name.padEnd(52).slice(0,52)} ${r.latency_ms}ms  → ${r.outcome}`, color);
        chime(r.outcome);
        if (r.detail && r.outcome !== "PASS") {
          push(`    ↳ ${String(r.detail).slice(0,160)}`, color);
        }
        await sleep(380);
      }
      push("", "");
      push(`↳ Total: ${data.total}  |  Pass: ${data.pass_count}  |  Warn: ${data.warn_count}  |  Fail: ${data.fail_count || 0}`, "brass");
      push(data.passed ? "✦ Verdict: RESILIENT — Deploy unlocked." : "✗ Verdict: BRITTLE — patch & re-run.",
           data.passed ? "brass" : "err");
      await refresh();
      if (data.passed) { showToast("Chaos survived · Deploy unlocked"); chime("PASS"); }
      else { chime("FAIL"); }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "unknown";
      push("⚠ Harness failed: " + detail, "err");
      chime("FAIL");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="phase-chaos">
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 22, alignItems: "flex-end" }}>
        <div>
          <div className="overline">Phase IV · Chaos Engineering — the crucible</div>
          <h3 className="font-serif" style={{ fontSize: 30, margin: "4px 0 0" }}>
            Break it first. Survive.
          </h3>
          <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", marginTop: 6 }}>
            Network, database, and edge-case perturbations are injected.
            The Deploy button stays sealed until the artifact endures.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            data-testid="chaos-sound-toggle"
            className="btn-ghost"
            onClick={toggleSound}
            title={soundOn ? "Chaos sound: ON (click to silence)" : "Chaos sound: OFF (click to enable)"}
            style={{ padding: "10px 14px", fontSize: 11 }}
          >
            {soundOn ? "♪ SFX ON" : "♪ SFX OFF"}
          </button>
          <button data-testid="chaos-run-btn" className="btn-brass" onClick={run} disabled={running}>
            {running ? "Injecting failures..." : chaos.passed ? "Re-run" : "Inject Failures"}
          </button>
        </div>
      </div>

      <div className={`terminal ${running ? "glitch" : ""}`} data-testid="chaos-terminal">
        {lines.length === 0 && !chaos.results?.length && (
          <span className="line muted">// terminal idle — awaiting injection //</span>
        )}
        {lines.length === 0 && chaos.results?.length > 0 && chaos.results.map((r, i) => {
          const cls = r.outcome === "PASS" ? "" : r.outcome === "WARN" ? "warn" : "err";
          return <span key={i} className={`line ${cls}`}>{`  [${r.layer.padEnd(10)}] ${r.name.padEnd(38)} ${r.latency_ms}ms  → ${r.outcome}`}</span>;
        })}
        {lines.map((l, i) => (
          <span key={i} className={`line ${l.cls}`}>{l.text}</span>
        ))}
      </div>

      {/* Runtime-stdout panel (only when a RUNTIME probe ran and captured stdout) */}
      {runtimeStdout.length > 0 && (
        <div data-testid="runtime-stdout-panel" style={{ marginTop: 14 }}>
          <button
            data-testid="runtime-stdout-toggle"
            className="btn-ghost"
            onClick={() => setStdoutOpen((v) => !v)}
            style={{ padding: "8px 14px", fontSize: 10, letterSpacing: "0.2em" }}
          >
            {stdoutOpen ? "▾" : "▸"} RUNTIME STDOUT · {runtimeStdout.length}
          </button>
          {stdoutOpen && (
            <div
              data-testid="runtime-stdout-body"
              className="terminal"
              style={{
                marginTop: 10,
                maxHeight: 220,
                fontSize: 12,
                borderColor: "rgba(74,222,128,0.35)",
              }}
            >
              <span className="line muted">// sandboxed Node vm.Script captured these writes to console.* //</span>
              {runtimeStdout.map((s, i) => (
                <span key={i} className="line">{`  › ${s}`}</span>
              ))}
              <span className="line muted">{`// source: ${runtimeProbe?.name || "VM Sandbox Runtime"} //`}</span>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)", letterSpacing: "0.15em" }}>
          STATUS:{" "}
          <span data-testid="chaos-status" style={{ color: chaos.passed ? "var(--success)" : "var(--warning)" }}>
            {chaos.passed ? "✓ RESILIENT — Deploy unlocked" : "◎ UNTESTED — Deploy sealed"}
          </span>
        </div>
      </div>
    </div>
  );
}
function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

/* ---------------- PHASE 5 — Deploy ---------------- */
function PhaseDeploy({ project, refresh }) {
  const [busy, setBusy] = useState(false);
  const [exported, setExported] = useState(project.deployed || false);
  const [providerModal, setProviderModal] = useState(null);
  const [vercelConnected, setVercelConnected] = useState(false);
  const [railwayConnected, setRailwayConnected] = useState(false);
  const [githubConnected, setGithubConnected] = useState(false);
  const [vercelDeploy, setVercelDeploy] = useState(project.vercel_deployment || null);
  const [railwayDeploy, setRailwayDeploy] = useState(project.railway_deployment || null);
  const [githubDeploy, setGithubDeploy] = useState(project.github_deployment || null);
  const [vercelBusy, setVercelBusy] = useState(false);
  const [railwayBusy, setRailwayBusy] = useState(false);
  const [githubBusy, setGithubBusy] = useState(false);
  const [vercelError, setVercelError] = useState("");
  const [railwayError, setRailwayError] = useState("");
  const [githubError, setGithubError] = useState("");
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsBusy, setLogsBusy] = useState(false);
  const fakeUrl = `https://lyceum.hexis-os.app/${project.project_id.slice(0, 10)}`;

  const canDeploy = project.chaos?.passed && (project.generation?.files?.length || 0) > 0;

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setVercelConnected(!!data.vercel_connected);
      setRailwayConnected(!!data.railway_connected);
      setGithubConnected(!!data.github_connected);
    }).catch(() => {});
  }, []);

  useEffect(() => { setVercelDeploy(project.vercel_deployment || null); }, [project.vercel_deployment]);
  useEffect(() => { setRailwayDeploy(project.railway_deployment || null); }, [project.railway_deployment]);
  useEffect(() => { setGithubDeploy(project.github_deployment || null); }, [project.github_deployment]);

  const downloadZip = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("hexis_token");
      const res = await fetch(`${API}/projects/${project.project_id}/export`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.name.replace(/[^a-z0-9-_]/gi, "_")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setExported(true);
      return true;
    } finally { setBusy(false); }
  };

  const deployVercelReal = async () => {
    setVercelBusy(true); setVercelError("");
    try {
      const { data } = await api.post(`/projects/${project.project_id}/deploy/vercel`);
      setVercelDeploy(data);
      if (refresh) await refresh();
    } catch (ex) {
      setVercelError(ex?.response?.data?.detail || "Vercel deploy failed");
    } finally { setVercelBusy(false); }
  };

  const deployRailwayReal = async () => {
    setRailwayBusy(true); setRailwayError("");
    try {
      const { data } = await api.post(`/projects/${project.project_id}/deploy/railway`);
      setRailwayDeploy(data);
      if (refresh) await refresh();
    } catch (ex) {
      setRailwayError(ex?.response?.data?.detail || "Railway project creation failed");
    } finally { setRailwayBusy(false); }
  };

  const pushToGithub = async () => {
    setGithubBusy(true); setGithubError("");
    try {
      const { data } = await api.post(`/projects/${project.project_id}/deploy/github`);
      setGithubDeploy(data);
      if (refresh) await refresh();
    } catch (ex) {
      setGithubError(ex?.response?.data?.detail || "GitHub push failed");
    } finally { setGithubBusy(false); }
  };

  const [rgBusy, setRgBusy] = useState(false);
  const [rgError, setRgError] = useState("");
  const deployRailwayViaGithub = async () => {
    setRgBusy(true); setRgError("");
    try {
      const { data } = await api.post(`/projects/${project.project_id}/deploy/railway-via-github`);
      // Open Railway's import wizard pre-filled with our repo
      if (data.import_url) {
        window.open(data.import_url, "_blank", "noopener,noreferrer");
      }
      if (refresh) await refresh();
    } catch (ex) {
      setRgError(ex?.response?.data?.detail || "Railway via GitHub failed");
    } finally { setRgBusy(false); }
  };

  const fetchLogs = async () => {
    setLogsBusy(true);
    try {
      const { data } = await api.get(`/projects/${project.project_id}/deploy/vercel/logs`);
      setLogs(data.events || []);
      setLogsOpen(true);
    } catch (ex) {
      alert("Logs fetch failed: " + (ex?.response?.data?.detail || "unknown"));
    } finally { setLogsBusy(false); }
  };

  return (
    <div data-testid="phase-deploy">
      <div className="overline">Phase V · Deploy — the sealed artifact</div>
      <h3 className="font-serif" style={{ fontSize: 36, margin: "8px 0 20px" }}>
        The rite is <span className="font-display" style={{ color: "var(--brass)" }}>complete</span>.
      </h3>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 28 }}>
        <div
          className="glass reveal"
          style={{ padding: 36, textAlign: "center", border: "1px solid var(--brass)" }}
          data-testid="deploy-certificate"
        >
          <svg width="64" height="64" viewBox="0 0 64 64" style={{ margin: "0 auto 18px" }}>
            <polygon points="32,6 56,19 56,45 32,58 8,45 8,19" fill="none" stroke="var(--brass)" strokeWidth="1.2" />
            <polygon points="32,16 46,23 46,41 32,48 18,41 18,23" fill="none" stroke="var(--brass)" strokeWidth="0.6" opacity="0.7" />
            <text x="32" y="38" textAnchor="middle" fill="var(--brass)" style={{ font: 'italic 20px "Playfair Display", serif' }}>✦</text>
          </svg>
          <div className="overline" style={{ marginBottom: 10 }}>Certificate of Passage</div>
          <h4 className="font-serif" style={{ fontSize: 30, margin: "0 0 8px" }}>{project.name}</h4>
          <p className="font-display" style={{ color: "var(--parchment-dim)", fontStyle: "italic" }}>
            Has survived the Six Disciplines and the Crucible of Chaos.
          </p>

          <div style={{ margin: "24px 0", display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
            <Stat label="Audit" value={`${Object.values(project.audit || {}).filter(v => v?.content).length}/6`} />
            <Stat label="Chaos" value={project.chaos?.passed ? "PASS" : "—"} color={project.chaos?.passed ? "var(--success)" : "var(--warning)"} />
            <Stat label="Files" value={(project.generation?.files?.length || 0) + ""} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
            <button
              data-testid="deploy-zip-btn"
              className="btn-brass"
              disabled={!canDeploy || busy}
              onClick={downloadZip}
              style={{ padding: "16px 36px", fontSize: 13, minWidth: 280 }}
            >
              {busy ? "Sealing..." : exported ? "⬇ Re-download ZIP" : "⬇ Download ZIP"}
            </button>

            {vercelConnected ? (
              <button
                data-testid="deploy-vercel-real-btn"
                className="btn-brass"
                disabled={!canDeploy || vercelBusy}
                onClick={deployVercelReal}
                style={{ padding: "14px 28px", fontSize: 12, minWidth: 280, background: "var(--brass)", color: "var(--ink-void)" }}
              >
                {vercelBusy ? "▲ Shipping to Vercel..." : vercelDeploy?.url ? "▲ Re-deploy to Vercel" : "▲ One-Click Deploy to Vercel"}
              </button>
            ) : (
              <button
                data-testid="deploy-vercel-btn"
                className="btn-ghost"
                disabled={!canDeploy || busy}
                onClick={async () => { await downloadZip(); setProviderModal("vercel-manual"); }}
                style={{ padding: "14px 22px", minWidth: 280 }}
              >
                ▲ Deploy to Vercel (connect token for one-click)
              </button>
            )}

            {railwayConnected ? (
              <button
                data-testid="deploy-railway-real-btn"
                className="btn-brass"
                disabled={!canDeploy || railwayBusy}
                onClick={deployRailwayReal}
                style={{ padding: "14px 28px", fontSize: 12, minWidth: 280, background: "var(--brass)", color: "var(--ink-void)" }}
              >
                {railwayBusy ? "🚂 Creating Railway project..." : railwayDeploy?.dashboard_url ? "🚂 Re-create Railway project" : "🚂 One-Click Create Railway Project"}
              </button>
            ) : (
              <button
                data-testid="deploy-railway-btn"
                className="btn-ghost"
                disabled={!canDeploy || busy}
                onClick={async () => { await downloadZip(); setProviderModal("railway"); }}
                style={{ padding: "14px 22px", minWidth: 280 }}
              >
                🚂 Deploy to Railway (connect token for one-click)
              </button>
            )}

            {githubConnected ? (
              <button
                data-testid="deploy-github-real-btn"
                className="btn-brass"
                disabled={!canDeploy || githubBusy}
                onClick={pushToGithub}
                style={{ padding: "14px 28px", fontSize: 12, minWidth: 280, background: "var(--brass)", color: "var(--ink-void)" }}
              >
                {githubBusy ? "⌘ Inscribing to GitHub..." : githubDeploy?.repo_url ? "⌘ Re-push to new Repo" : "⌘ One-Click Push to GitHub"}
              </button>
            ) : (
              <button
                data-testid="deploy-github-btn"
                className="btn-ghost"
                disabled={!canDeploy || busy}
                onClick={async () => { await downloadZip(); setProviderModal("github"); }}
                style={{ padding: "14px 22px", minWidth: 280 }}
              >
                ⌘ Push to GitHub (connect PAT for one-click)
              </button>
            )}

            {/* Composite: Push to GitHub + open Railway import wizard in one click.
                Available only when GitHub is connected (the pipeline requires the repo first). */}
            {githubConnected && (
              <button
                data-testid="deploy-railway-via-github-btn"
                className="btn-ghost"
                disabled={!canDeploy || rgBusy}
                onClick={deployRailwayViaGithub}
                style={{ padding: "14px 22px", minWidth: 280 }}
                title="Pushes the sprint to GitHub, then opens Railway's 'Deploy from GitHub repo' wizard pre-filled with your repo URL"
              >
                {rgBusy ? "⌘→🚂 Chaining..." : "⌘ → 🚂 GitHub + Railway (one click)"}
              </button>
            )}
          </div>

          {!canDeploy && (
            <div data-testid="deploy-locked-reason" className="font-mono" style={{ marginTop: 14, fontSize: 11, color: "var(--warning)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
              {project.chaos?.passed ? "Generate code first" : "Chaos test must pass first"}
            </div>
          )}

          {/* Secondary actions: PDF + Seal link */}
          <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(197,160,89,0.2)", display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              data-testid="deploy-pdf-btn"
              className="btn-ghost"
              onClick={async () => {
                const token = localStorage.getItem("hexis_token");
                const res = await fetch(`${API}/projects/${project.project_id}/audit.pdf`, {
                  credentials: "include",
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) { alert("PDF failed"); return; }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `${project.name.replace(/[^a-z0-9-_]/gi, "_")}_hexis_audit.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              }}
              style={{ padding: "10px 18px", fontSize: 10 }}
            >
              ⬇ Audit PDF
            </button>
            <CopySealLinkButton projectId={project.project_id} />
          </div>
        </div>

        {/* Status panel */}
        <div className="glass" style={{ padding: 28 }}>
          {vercelDeploy?.url ? (
            <>
              <div className="overline" style={{ marginBottom: 14, color: "var(--success)" }}>✓ Live on Vercel</div>
              <h4 className="font-serif" style={{ fontSize: 24, margin: "0 0 14px" }}>Your artifact is deployed.</h4>
              <a
                data-testid="vercel-deployed-url"
                href={vercelDeploy.url}
                target="_blank" rel="noreferrer"
                className="font-mono"
                style={{
                  display: "block", padding: 14, background: "rgba(9,10,15,0.6)",
                  border: "1px solid var(--success)", color: "var(--terminal)",
                  fontSize: 12, letterSpacing: "0.05em", wordBreak: "break-all",
                  textDecoration: "none",
                }}
              >
                {vercelDeploy.url}
              </a>
              <div style={{ marginTop: 14, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                {vercelDeploy.inspector_url && (
                  <a data-testid="vercel-inspector-url" href={vercelDeploy.inspector_url} target="_blank" rel="noreferrer" className="link-brass font-mono"
                    style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                    Open Vercel Dashboard →
                  </a>
                )}
                <button data-testid="vercel-logs-btn" className="btn-ghost" onClick={fetchLogs} disabled={logsBusy} style={{ padding: "8px 14px", fontSize: 10 }}>
                  {logsBusy ? "Fetching..." : logsOpen ? "Refresh Logs" : "📜 Build Logs"}
                </button>
              </div>

              {logsOpen && (
                <div style={{ marginTop: 16 }}>
                  <div className="overline" style={{ marginBottom: 8 }}>Build Events · {logs.length}</div>
                  <div
                    data-testid="vercel-logs-terminal"
                    className="terminal"
                    style={{ maxHeight: 260, fontSize: 11 }}
                  >
                    {logs.length === 0 ? (
                      <span className="line muted">// no events yet — Vercel may still be building //</span>
                    ) : (
                      logs.map((ev, i) => {
                        const cls = ev.level === "error" ? "err" : ev.level === "warning" ? "warn" : "";
                        const prefix = ev.type ? `[${ev.type}] ` : "";
                        const txt = String(ev.text || "").slice(0, 500);
                        return <span key={i} className={`line ${cls}`}>{prefix}{txt}</span>;
                      })
                    )}
                  </div>
                </div>
              )}
            </>
          ) : railwayDeploy?.dashboard_url ? (
            <>
              <div className="overline" style={{ marginBottom: 14, color: "var(--success)" }}>✓ Railway Project Created</div>
              <h4 className="font-serif" style={{ fontSize: 24, margin: "0 0 14px" }}>
                Project <em style={{ color: "var(--brass)" }}>{railwayDeploy.project_name}</em> is ready.
              </h4>
              <a
                data-testid="railway-deployed-url"
                href={railwayDeploy.dashboard_url}
                target="_blank" rel="noreferrer"
                className="font-mono"
                style={{
                  display: "block", padding: 14, background: "rgba(9,10,15,0.6)",
                  border: "1px solid var(--success)", color: "var(--terminal)",
                  fontSize: 12, letterSpacing: "0.05em", wordBreak: "break-all",
                  textDecoration: "none",
                }}
              >
                {railwayDeploy.dashboard_url}
              </a>
              <p className="font-display" style={{ marginTop: 16, color: "var(--muted)", fontSize: 13, fontStyle: "italic", lineHeight: 1.55 }}>
                Fig. V — Railway's API creates project shells only. Open the dashboard and
                connect your source (drag-drop ZIP, link GitHub, or deploy a Docker image).
                <br /><br />
                <em>The <code style={{ color: "var(--brass)" }}>railway.json</code> in your ZIP is pre-configured.</em>
              </p>
            </>
          ) : githubDeploy?.repo_url ? (
            <>
              <div className="overline" style={{ marginBottom: 14, color: "var(--success)" }}>✓ Inscribed to GitHub</div>
              <h4 className="font-serif" style={{ fontSize: 24, margin: "0 0 14px" }}>
                Repository <em style={{ color: "var(--brass)" }}>{githubDeploy.owner}/{githubDeploy.repo_name}</em> is live.
              </h4>
              <a
                data-testid="github-deployed-url"
                href={githubDeploy.repo_url}
                target="_blank" rel="noreferrer"
                className="font-mono"
                style={{
                  display: "block", padding: 14, background: "rgba(9,10,15,0.6)",
                  border: "1px solid var(--success)", color: "var(--terminal)",
                  fontSize: 12, letterSpacing: "0.05em", wordBreak: "break-all",
                  textDecoration: "none",
                }}
              >
                {githubDeploy.repo_url}
              </a>
              <div style={{ marginTop: 14, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                {githubDeploy.commit_url && (
                  <a data-testid="github-commit-url" href={githubDeploy.commit_url} target="_blank" rel="noreferrer" className="link-brass font-mono"
                    style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                    View commit {String(githubDeploy.commit_sha || "").slice(0, 7)} →
                  </a>
                )}
                <span className="font-mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                  {githubDeploy.files_count} files · {githubDeploy.default_branch || "main"}
                </span>
              </div>
              <p className="font-display" style={{ marginTop: 16, color: "var(--muted)", fontSize: 13, fontStyle: "italic", lineHeight: 1.55 }}>
                Fig. VI — A private repository was consecrated and every generated file
                inscribed in one atomic commit alongside the <code style={{ color: "var(--brass)" }}>HEXIS_MANIFEST.json</code>.
              </p>
            </>
          ) : vercelError || railwayError || githubError || rgError ? (
            <>
              <div className="overline" style={{ marginBottom: 14, color: "var(--warning)" }}>⚠ Deploy Failed</div>
              <div data-testid="deploy-error-detail" className="font-mono" style={{ fontSize: 12, color: "var(--warning)", lineHeight: 1.6, padding: 14, background: "rgba(153, 27, 27, 0.1)", border: "1px solid var(--warning)" }}>
                {vercelError || railwayError || githubError || rgError}
              </div>
              <p className="font-display" style={{ marginTop: 16, color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>
                Verify your token at <a href="/settings" className="link-brass">Settings</a>.
              </p>
            </>
          ) : (
            <>
              <div className="overline" style={{ marginBottom: 14 }}>Mock Deployment</div>
              <div className="font-serif" style={{ fontSize: 18, lineHeight: 1.55, color: "var(--parchment-dim)", fontStyle: "italic", marginBottom: 18 }}>
                {vercelConnected || railwayConnected
                  ? "Pick a one-click button to ship for real."
                  : "Connect Vercel or Railway in Settings for real one-click deploy. Otherwise:"}
              </div>
              <div
                data-testid="deploy-mock-url"
                className="font-mono"
                style={{
                  padding: 14, background: "rgba(9,10,15,0.6)",
                  border: "1px solid rgba(74,222,128,0.3)", color: "var(--terminal)",
                  fontSize: 12, letterSpacing: "0.05em", wordBreak: "break-all",
                }}
              >
                {fakeUrl}
              </div>
              <p className="font-display" style={{ marginTop: 16, color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>
                Fig. V — Mock URL. ZIP contains{" "}
                <span style={{ color: "var(--brass)" }}>Dockerfile</span>,{" "}
                <span style={{ color: "var(--brass)" }}>vercel.json</span>,{" "}
                <span style={{ color: "var(--brass)" }}>railway.json</span>.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Seal Analytics + Visibility */}
      <SealAnalyticsCard project={project} />

      {providerModal && (
        <DeployProviderModal
          provider={providerModal === "vercel-manual" ? "vercel" : providerModal}
          project={project}
          onClose={() => setProviderModal(null)}
        />
      )}
    </div>
  );
}

function DeployProviderModal({ provider, project, onClose }) {
  const cfg = {
    vercel:  { name: "Vercel",  icon: "▲", importUrl: "https://vercel.com/new",          configFile: "vercel.json" },
    railway: { name: "Railway", icon: "🚂", importUrl: "https://railway.app/new",          configFile: "railway.json" },
    github:  { name: "GitHub",  icon: "⌘", importUrl: "https://github.com/new",           configFile: "HEXIS_MANIFEST.json" },
  };
  const c = cfg[provider] || cfg.vercel;
  const providerName = c.name;
  const providerIcon = c.icon;
  const importUrl = c.importUrl;
  const isGithub = provider === "github";

  return (
    <div
      data-testid={`deploy-${provider}-modal`}
      style={{
        position: "fixed", inset: 0, background: "rgba(9,10,15,0.88)",
        zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center",
        backdropFilter: "blur(6px)", padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass"
        style={{ width: "min(640px, 94vw)", padding: 44, border: "1px solid var(--brass)" }}
      >
        <div className="overline" style={{ marginBottom: 14 }}>{providerName} · Deployment</div>
        <h3 className="font-serif" style={{ fontSize: 34, margin: "0 0 10px" }}>
          {providerIcon} {isGithub ? `Push to ${providerName}.` : `Ship to ${providerName}.`}
        </h3>
        <p className="font-serif" style={{ fontSize: 17, fontStyle: "italic", color: "var(--parchment-dim)", lineHeight: 1.55, marginBottom: 22 }}>
          Your ZIP download has begun.{" "}
          {isGithub
            ? "For one-click push, connect a GitHub PAT in Settings. Otherwise, push the ZIP manually:"
            : "Follow these three steps to complete the real deploy:"}
        </p>
        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          {(isGithub
            ? [
                ["Extract & init", "Unzip the archive, run `git init && git add -A && git commit -m 'hexis passage'`."],
                ["Create repo", "Open github.com/new in the new tab and create a private repo (any name)."],
                ["Push", "Run `git remote add origin <repo-url> && git branch -M main && git push -u origin main`."],
              ]
            : [
                ["Extract & init", "Unzip the archive, run `git init && git add -A && git commit -m 'hexis passage'`."],
                ["Push to GitHub", "Create a repo on github.com — push your local commits (`git push -u origin main`)."],
                [`Import on ${providerName}`, `Open ${providerName}'s import page in the new tab and connect your repo. The \`${c.configFile}\` is pre-configured.`],
              ]
          ).map(([t, d], i) => (
            <li key={i} style={{ display: "grid", gridTemplateColumns: "44px 1fr", gap: 14 }}>
              <span className="num-stamp" style={{ width: 36, height: 36, fontSize: 15 }}>{i + 1}</span>
              <div>
                <div className="font-serif" style={{ fontSize: 20, color: "var(--parchment)" }}>{t}</div>
                <div className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)", marginTop: 2, lineHeight: 1.55 }}>{d}</div>
              </div>
            </li>
          ))}
        </ol>
        <div style={{ marginTop: 28, display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button className="btn-ghost" onClick={onClose} data-testid="deploy-modal-close">Close</button>
          <a
            data-testid={`open-${provider}-btn`}
            className="btn-brass"
            href={importUrl}
            target="_blank" rel="noreferrer"
            style={{ textDecoration: "none" }}
            onClick={() => setTimeout(onClose, 300)}
          >
            Open {providerName} →
          </a>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color = "var(--parchment)" }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="font-serif" style={{ fontSize: 32, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function SealAnalyticsCard({ project }) {
  const [stats, setStats] = useState(null);
  const [visibility, setVisibility] = useState(project.seal_visibility || "public");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${project.project_id}/seal-stats`);
      setStats(data);
      setVisibility(data.visibility || "public");
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.project_id]);

  const toggle = async () => {
    const next = visibility === "public" ? "private" : "public";
    setBusy(true);
    try {
      await api.put(`/projects/${project.project_id}/seal-visibility`, { visibility: next });
      setVisibility(next);
      await load();
    } finally { setBusy(false); }
  };

  const isPrivate = visibility === "private";

  return (
    <div
      data-testid="seal-analytics-card"
      className="glass"
      style={{ marginTop: 24, padding: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, alignItems: "center" }}
    >
      <div>
        <div className="overline" style={{ marginBottom: 10 }}>Seal Analytics</div>
        <div style={{ display: "flex", gap: 38, alignItems: "baseline" }}>
          <div>
            <div className="font-display" data-testid="seal-views-count" style={{ fontSize: 48, color: "var(--brass)", lineHeight: 1 }}>
              {stats?.view_count ?? "—"}
            </div>
            <div className="overline" style={{ fontSize: 9, marginTop: 4 }}>Views</div>
          </div>
          <div>
            <div className="font-display" data-testid="seal-shares-count" style={{ fontSize: 48, color: "var(--brass)", lineHeight: 1 }}>
              {stats?.share_count ?? "—"}
            </div>
            <div className="overline" style={{ fontSize: 9, marginTop: 4 }}>Shares</div>
          </div>
        </div>

        {/* 14-day view-count sparkline */}
        <ViewSparkline history={stats?.view_history || []} />

        <div className="font-mono" style={{ marginTop: 10, fontSize: 10, color: "var(--muted)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
          {stats?.last_viewed_at ? `last view ${new Date(stats.last_viewed_at).toUTCString().slice(5, 16)}` : "no views yet"}
        </div>
      </div>

      <div style={{ borderLeft: "1px solid rgba(197,160,89,0.2)", paddingLeft: 28 }}>
        <div className="overline" style={{ marginBottom: 10 }}>Seal Visibility</div>
        <div className="font-serif" style={{ fontSize: 20, marginBottom: 6, fontStyle: "italic" }}>
          {isPrivate ? "Private — only you can view." : "Public — anyone with the link can view & download."}
        </div>
        <div className="font-mono" style={{ fontSize: 11, color: "var(--parchment-dim)", marginBottom: 16, lineHeight: 1.6 }}>
          Private seals block anonymous access to /seal/:id, OG image, PDF, and ZIP downloads.
        </div>
        <button
          data-testid="seal-visibility-toggle"
          className={isPrivate ? "btn-brass" : "btn-ghost"}
          disabled={busy}
          onClick={toggle}
          style={{ padding: "10px 22px" }}
        >
          {busy ? "Updating..." : isPrivate ? "🔓 Make Public" : "🔒 Make Private"}
        </button>
      </div>
    </div>
  );
}

/* 14-day daily-view sparkline, rendered as inline SVG. No deps. */
function ViewSparkline({ history }) {
  // Aggregate ISO-timestamp list into per-UTC-day buckets for the last 14 days (including today).
  const days = 14;
  const buckets = Array(days).fill(0);
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  for (const iso of history || []) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) continue;
    const dayStart = new Date(d);
    dayStart.setUTCHours(0, 0, 0, 0);
    const delta = Math.floor((today - dayStart) / 86400000);
    if (delta >= 0 && delta < days) buckets[days - 1 - delta] += 1;
  }
  const max = Math.max(1, ...buckets);
  const w = 240, h = 42, pad = 2;
  const step = (w - pad * 2) / (days - 1);
  const pts = buckets.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - (v / max) * (h - pad * 2);
    return [x, y];
  });
  const linePath = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${pts[pts.length - 1][0].toFixed(1)},${h - pad} L${pts[0][0].toFixed(1)},${h - pad} Z`;
  const total14 = buckets.reduce((s, v) => s + v, 0);
  return (
    <div data-testid="seal-views-sparkline" style={{ marginTop: 14 }}>
      <div className="font-mono" style={{ fontSize: 9, color: "var(--parchment-dim)", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>
        Last 14 days · {total14} view{total14 === 1 ? "" : "s"}
      </div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
        <defs>
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(197,160,89,0.45)" />
            <stop offset="100%" stopColor="rgba(197,160,89,0.02)" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#sparkFill)" />
        <path d={linePath} stroke="var(--brass)" strokeWidth="1.5" fill="none" />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={buckets[i] > 0 ? 1.6 : 0.6} fill="var(--brass)" opacity={buckets[i] > 0 ? 0.95 : 0.3} />
        ))}
      </svg>
    </div>
  );
}

function CopySealLinkButton({ projectId }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/api/seal/${projectId}/share`;
  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const shareOnX = () => {
    const text = "Forged a sprint in the HEXIS Lyceum — six disciplines, one seal.";
    const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(tweet, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  return (
    <>
      <button
        data-testid="deploy-copy-seal-btn"
        className="btn-ghost"
        onClick={copy}
        style={{ padding: "10px 18px", fontSize: 10 }}
      >
        {copied ? "✓ Copied" : "✦ Copy Seal Link"}
      </button>
      <button
        data-testid="deploy-share-x-btn"
        className="btn-ghost"
        onClick={shareOnX}
        style={{ padding: "10px 18px", fontSize: 10 }}
        title="Post this seal to X / Twitter"
      >
        𝕏 Share on X
      </button>
      <a
        data-testid="deploy-open-seal-btn"
        href={`/seal/${projectId}`}
        target="_blank" rel="noreferrer"
        className="btn-ghost"
        style={{ padding: "10px 18px", fontSize: 10, textDecoration: "none" }}
      >
        Open Seal →
      </a>
    </>
  );
}

/* ---------------- Shared ---------------- */
function ModelToggle({ model, setModel, inline = false }) {
  return (
    <div
      data-testid="model-toggle"
      style={{
        display: "flex", border: "1px solid rgba(197,160,89,0.3)",
        fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.2em",
      }}
    >
      {[
        { k: "claude", label: "Claude · Speed" },
        { k: "deepseek", label: "DeepSeek · Logic" },
      ].map((o) => (
        <button
          key={o.k}
          data-testid={`model-${o.k}`}
          onClick={() => setModel(o.k)}
          style={{
            background: model === o.k ? "var(--brass)" : "transparent",
            color: model === o.k ? "var(--ink-void)" : "var(--parchment-dim)",
            border: "none", padding: "10px 14px", cursor: "pointer",
            textTransform: "uppercase", fontFamily: "inherit", fontSize: inline ? 10 : 10,
            letterSpacing: "0.2em",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
